import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewContainerRef,
  effect,
  input,
  output,
  OutputRefSubscription,
  signal,
  Signal,
  viewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DNComment } from '../../../../domain/content/content.model';
import { WidgetInjectorDirective } from '../../../util/directives/widget-injector/widget-injector.directive';
import { CommentBoxComponent } from '../../comment-box/comment-box.component';
import { CreateCommentDTO } from '../../comments.component';
import { CommentsService } from '../../comments.service';
import { CommentHistoryComponent } from '../comment-history.component';
import { SigninDirective } from '../../../util/directives/signin/signing-directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DatePipe } from '@angular/common';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

const MAX_NUM_REPLIES = 2;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-comment-bubble',
  templateUrl: 'comment-bubble.component.html',
  styleUrls: ['comment-bubble.component.scss'],
  imports: [MatFormFieldModule, MatInputModule, TextFieldModule, MatButtonModule, MatIconModule, SigninDirective, WidgetInjectorDirective, DatePipe]
})
export class CommentBubbleComponent implements OnDestroy {
  // Current comment for this bubble
  readonly comment = input<DNComment>(undefined);
  // Nuovo input Signal per le risposte, sostituisce l'array precedente
  readonly repliesSignal = input<Signal<DNComment[]>>();
  // Keep track of indentationLevel to make other children indent correctly
  readonly indentLevel = input<number>(undefined);
  // Manage if the bubble can show more comments/allowed to get replied
  readonly areActionsEnabled = input<boolean>(true);

  // Aggiungi un nuovo input per ricevere l'emitter dal genitore
  readonly replyBus = input<EventEmitter<DNComment>>();
  readonly submitCommentBus = input<EventEmitter<CreateCommentDTO>>();


  // Show the commentBox
  // @Output() readonly showReplyBoxEmitter2: EventEmitter<{
  //   viewRef: ViewContainerRef;
  //   compRef: ComponentRef<CommentBoxComponent>;
  // }> = new EventEmitter();


  // Show the commentBox
  readonly showReplyBoxEmitter = output<{
    viewRef: ViewContainerRef;
    compRef: ComponentRef<CommentBoxComponent>;
  }>();

  //readonly loadHistoryEmitter = output<DNComment>();
  readonly submitCommentEmitter = output<CreateCommentDTO>();

  readonly injectionPoint = viewChild.required(WidgetInjectorDirective);

  // UI vars
  permissionDialogTitle: string = this.ts.instant('dottnet.permessi.commentPermission');

  maxNumReplies: number = MAX_NUM_REPLIES;
  showMoreLabel: string = '';
  replyLabel: string = this.ts.instant('dottnet.field.label.comments.reply');

  // Child components refs
  commentBoxRef: ComponentRef<CommentBoxComponent> = undefined;
  historyRef: ComponentRef<CommentHistoryComponent> = undefined;

  // Subs
  historyRefSubmitSub: OutputRefSubscription;
  commentBoxRefSubmitSub: OutputRefSubscription;

  constructor(
    private ts: TranslateService,
    private matDialog: MatDialog,
    private commentsService: CommentsService
  ) {
    // Aggiungi un effect per gestire i cambiamenti di replies signal
    effect(() => {
      // Solo se il historyRef è definito e abbiamo un repliesSignal
      if (this.historyRef && this.repliesSignal()) {
        // Otteniamo i valori attuali dal signal e li impostiamo
        const currentReplies = this.repliesSignal()();
        this.historyRef.instance.commentsInternal.set(currentReplies);
      }

      // Aggiorna l'etichetta showMoreLabel
      if (this.comment()) {
        this.showMoreLabel = this.ts.instant('dottnet.field.label.comments.showMore', {
          numCommenti: this.comment().numRisposte
        });
      }
    });
  }


  ngOnDestroy(): void {
    if (this.historyRefSubmitSub) this.historyRefSubmitSub.unsubscribe();
    if (this.commentBoxRefSubmitSub) this.commentBoxRefSubmitSub.unsubscribe();
  }

  // Create commentBox component if still undefined
  showCommentBox(comment: DNComment, indentLevel: number) {
    if (this.matDialog.openDialogs.length === 0) {
      // If this comment can use actions
      if (this.areActionsEnabled()) {
        if (!this.commentBoxRef) {
          // Create commentBox
          this.commentBoxRef = this.injectionPoint().viewContainerRef.createComponent<CommentBoxComponent>(
            CommentBoxComponent
          );

          this.commentBoxRef.instance.setLabelText(`${this.ts.instant('dottnet.field.label.comments.replying')} ${comment.autore}`);
          this.commentBoxRef.instance.indentLevel = indentLevel + 1;

          // Passa il submitCommentBus al commentBox
          this.commentBoxRef.setInput('submitCommentBus', this.submitCommentBus());

          // Subscribe to commentBox submitCommentEmitter
          this.commentBoxRefSubmitSub = this.commentBoxRef.instance.submitCommentEmitter.subscribe(
            ({ commentText, attachmentTemplate }) => {
              // Detach commentBox
              this.injectionPoint().viewContainerRef.detach(
                this.injectionPoint().viewContainerRef.indexOf(this.commentBoxRef.hostView)
              );
              // Reset its value
              this.commentBoxRef.instance.commentText.setValue('');

              // Emit from this component emitter to notify outer histories/comments-component
              // Usa il bus invece dell'output locale
              const commentValue = this.comment();
              const commentDTO: CreateCommentDTO = {
                commentText,
                attachmentTemplate,
                parentComment: commentValue
              };

              if (this.submitCommentBus()) {
                this.submitCommentBus().emit(commentDTO);
              }
            }
          );
        }

        this.showHistory(this.comment());

        // Insert commentBox at 0 position
        this.injectionPoint().viewContainerRef.insert(this.commentBoxRef.hostView, 0);

        // Communicate to parent to hide last commentBox
        this.showReplyBoxEmitter.emit({
          viewRef: this.injectionPoint().viewContainerRef,
          compRef: this.commentBoxRef
        });
      }
    }



  }

  // Show comment history component
  // Modifica il metodo showHistory
  showHistory(comment: DNComment) {
    if (this.areActionsEnabled()) {
      if (this.replyBus()) {
        this.replyBus().emit(comment);
      }

      if (!this.historyRef) {
        this.historyRef = this.injectionPoint().viewContainerRef.createComponent<CommentHistoryComponent>(
          CommentHistoryComponent
        );
      } else {
        this.injectionPoint().viewContainerRef.insert(this.historyRef.hostView);
      }

      this.historyRef.instance.indentLevel = this.indentLevel() + 1;

      // Passa entrambi i bus alla history component
      this.historyRef.setInput('replyBus', this.replyBus());
      this.historyRef.setInput('submitCommentBus', this.submitCommentBus());

      if (this.repliesSignal()) {
        this.historyRef.instance.commentsInternal.set(this.repliesSignal()());
      }

      // Aggiorna la subscription per usare il bus
      this.historyRefSubmitSub = this.historyRef.instance.submitCommentEmitter.subscribe(
        ({ commentText, parentComment, attachmentTemplate }) => {
          const commentDTO: CreateCommentDTO = {
            commentText,
            attachmentTemplate,
            parentComment
          };

          if (this.submitCommentBus()) {
            this.submitCommentBus().emit(commentDTO);
          }
        }
      );
    }
  }

  showAttachment(comment: DNComment) {
    this.commentsService.showAttachment(comment.idContenutoAllegato, comment.templateAllegato);
  }

  // If historyComponent is not already shown and we have more than 0 answers to this comment
  canShowMore() {
    return (
      this.injectionPoint().viewContainerRef.indexOf(this.historyRef?.hostView) === -1 &&
      this.comment().numRisposte > 0
    );
  }

  // If commentBox is not already shown
  canReply() {
    return (
      this.injectionPoint().viewContainerRef.indexOf(this.commentBoxRef?.hostView) === -1 &&
      this.indentLevel() < this.maxNumReplies
    );
  }
}
